/**
 * Inline editor for a project lane: name, DRI, support, ordering, active.
 *
 * Same absent-versus-null discipline as PhaseEditor - only touched fields are sent.
 * See that file's header for why it matters.
 *
 * DRI and Support are the two fields the workbook never had filled in. Eighteen
 * owner slots came across the migration null, because the name-per-project mapping
 * has never existed anywhere, and they were deliberately NOT guessed at load time.
 * This form is how they get filled in, one honest answer at a time, which is why the
 * roster dropdown offers "Unassigned" as a first-class choice rather than defaulting
 * to whoever happens to be first alphabetically.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { describeError, patchProject } from '../services/api';
import { palette } from '../styles/theme';
import { ErrorText, Hint, Input, Label, PrimaryButton, SecondaryButton, Select } from '../styles/ui';
import type { Person, Project, ProjectPatch } from '../types';

interface FormValues {
  name: string;
  dri_email: string;
  support_email: string;
  active: boolean;
}

const Form = styled.form`
  display: grid;
  grid-template-columns: minmax(180px, 1.4fr) minmax(160px, 1fr) minmax(160px, 1fr);
  gap: 12px 14px;
  align-items: start;
  padding: 14px 16px 16px 30px;
`;

const Actions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  flex: 1;
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${palette.ink};
  cursor: pointer;
`;

export function buildProjectPatch(
  values: FormValues,
  dirty: Partial<Record<keyof FormValues, boolean>>
): ProjectPatch {
  const patch: ProjectPatch = {};
  if (dirty.name) {
    patch.name = values.name.trim();
  }
  if (dirty.dri_email) {
    // The backend lowercases and turns '' into a real null, but doing it here too
    // means the value posted matches the value that comes back, so the lane does not
    // flicker from "Joe@..." to "joe@..." after a save.
    patch.dri_email = values.dri_email.trim().toLowerCase() || null;
  }
  if (dirty.support_email) {
    patch.support_email = values.support_email.trim().toLowerCase() || null;
  }
  if (dirty.active) {
    patch.active = values.active;
  }
  return patch;
}

interface ProjectEditorProps {
  project: Project;
  people: Person[];
  onSaved: (patch: ProjectPatch) => void;
  onCancel: () => void;
}

/** The roster dropdown, shared by both owner fields. */
function OwnerOptions({ people, current }: { people: Person[]; current: string | null }) {
  const known = people.some((p) => p.email === current);
  return (
    <>
      <option value="">Unassigned</option>
      {!known && current ? <option value={current}>{current} (not on the roster)</option> : null}
      {people.map((person) => (
        <option key={person.email} value={person.email}>
          {person.name}
          {person.active ? '' : ' (inactive)'}
        </option>
      ))}
    </>
  );
}

export default function ProjectEditor({ project, people, onSaved, onCancel }: ProjectEditorProps) {
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { dirtyFields, isSubmitting, isDirty, errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: project.name,
      dri_email: project.dri_email ?? '',
      support_email: project.support_email ?? '',
      active: project.active,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    const patch = buildProjectPatch(values, dirtyFields);
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    try {
      await patchProject(project.project_id, patch);
      // Merged field by field by the caller rather than replaced wholesale: the
      // PATCH response is ProjectOut and has no `phases`, so assigning it over the
      // lane would empty it. See services/api.ts.
      onSaved(patch);
    } catch (err) {
      setError(describeError(err));
    }
  });

  return (
    <Form onSubmit={onSubmit}>
      <Label>
        Project name
        <Input
          {...register('name', { required: 'A project needs a name.', maxLength: 200 })}
          aria-invalid={Boolean(errors.name)}
        />
      </Label>

      <Label>
        DRI
        <Select {...register('dri_email')}>
          <OwnerOptions people={people} current={project.dri_email} />
        </Select>
        <Hint>Directly responsible individual.</Hint>
      </Label>

      <Label>
        Support
        <Select {...register('support_email')}>
          <OwnerOptions people={people} current={project.support_email} />
        </Select>
      </Label>

      <Actions>
        <CheckLabel>
          <input type="checkbox" {...register('active')} />
          Active on the roadmap
        </CheckLabel>
        <Hint>Unticking hides the lane. Nothing is deleted.</Hint>
        <Spacer />
        {errors.name ? <ErrorText>{errors.name.message}</ErrorText> : null}
        {error ? <ErrorText role="alert">{error}</ErrorText> : null}
        <SecondaryButton type="button" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </SecondaryButton>
        <PrimaryButton type="submit" disabled={isSubmitting || !isDirty}>
          {isSubmitting ? 'Saving…' : 'Save project'}
        </PrimaryButton>
      </Actions>
    </Form>
  );
}
